import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import type { ArticleNumberRequestResponse, ListResponse } from '@proerp/shared';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@proerp/ui';
import { APP_VERSION } from '@/features/changelog/appVersion';
import { apiRequest } from '@/lib/apiClient';
import { useArticleChangeRequests } from '@/features/inventory/articleChangeRequestsApi';
import { usePublicBranding } from '@/features/settings/settingsApi';
import { usePermissions } from '@/lib/useCurrentUser';
import { useIsTouchDevice } from '@/lib/useIsTouchDevice';
import { NAV_GROUP_LANDING, NAV_GROUPS, navGroupSlug, type NavItem } from './nav';
import { NavUser } from './NavUser';
import { useNavGroupOrder, useNavItems } from './useNavItems';

/** Route, deren offene Anträge das Sekretariat als Zähler-Badge sieht. */
const ANTRAEGE_ROUTE = '/engineering/antraege';

/**
 * Zähler-Badge offener Artikel-Anträge — nur für Freigabeberechtigte
 * (engineering.approve). Gezählt werden BEIDE Antragsarten der Warteschlange:
 * Artikelnummern-Anträge (ADR 0031) und Änderungsanträge am Artikelstamm
 * (ADR 0089/VB-26016) — die Zahl muss zu dem passen, was die Anträge-Seite
 * zeigt. Wird gepollt; teilt sich den Query-Cache mit der Seite (gleiche
 * Query-Keys). Rendert nichts ohne Recht oder bei 0.
 *
 * Exportiert, damit das Abzeichen ohne die komplette Sidebar testbar ist.
 */
export function AntraegeNavBadge({ className = '' }: { className?: string }) {
  const { can } = usePermissions();
  const isApprover = can('engineering.approve');
  const { data: numberRequests } = useQuery({
    queryKey: ['article-number-requests', 'PENDING'],
    queryFn: () =>
      apiRequest<ListResponse<ArticleNumberRequestResponse>>(
        '/v1/article-number-requests?status=PENDING',
      ),
    enabled: isApprover,
    refetchInterval: 60_000,
  });
  // Derselbe Hook wie auf der Anträge-Seite — Abzeichen und Seite teilen sich
  // damit den Cache-Eintrag, und die Adresse steht nur an einer Stelle. Die
  // Liste hängt an `inventory.read`; fehlt das Recht, bliebe nur ein 403 übrig.
  const { data: changeRequests } = useArticleChangeRequests(
    { status: 'PENDING' },
    { enabled: isApprover && can('inventory.read'), refetchInterval: 60_000 },
  );
  const count = (numberRequests?.data.length ?? 0) + (changeRequests?.data.length ?? 0);
  if (!isApprover || count === 0) return null;
  return (
    <span
      className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground ${className}`}
      aria-label={`${count} offene Anträge`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** Aktiv-Logik: Dashboard nur exakt, sonst Pfad-Präfix (Detailseiten inklusive). */
function isPathActive(to: string, pathname: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

/** Für Eltern mit Unterpunkten: aktiv, sobald einer der Unterpunkte aktiv ist. */
function isItemActive(item: NavItem, pathname: string): boolean {
  if (item.children?.length) {
    return item.children.some((child) => isPathActive(child.to, pathname));
  }
  if (item.end) return pathname === item.to;
  return isPathActive(item.to, pathname);
}

const ACTIVE_CLASS =
  'data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground';

/** Eingeklappte Nav-Gruppen je Benutzer merken (Variante A). true = eingeklappt. */
const GROUP_STATE_KEY = 'proerp.nav.collapsed-groups.v1';
function loadCollapsedGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(GROUP_STATE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

/**
 * Hauptnavigation im Stil des shadcn-Blocks sidebar-07: Workspace-Kopf,
 * Einträge mit Unterpunkten klappen auf (Collapsible), Benutzermenü im Footer.
 * Rail-Klick und Strg/⌘+B klappen um. Nur Desktop — mobil übernimmt die
 * die mobile Leiste `MobileDock` (§5.2), Schwelle ist der eigene `tablet`-Breakpoint (744px, siehe
 * globals.css), nicht Tailwinds `md` (768px) — sonst bekäme das iPad mini im
 * Hochformat (744px) die Handy-Ansicht. Kollabieren geht auf Maus-Geräten nur
 * auf Icon-Breite (Tooltips zeigen dann die Labels); auf Touch-Geräten (iPad,
 * iPhone im Querformat — ab 744 px zeigt auch dort diese Sidebar statt der mobilen Leiste)
 * klappt sie stattdessen ganz weg, damit die Seite den vollen Bildschirm
 * bekommt (Nutzer-Feedback: Icon-Leiste bringt auf einem Tablet nichts, ein
 * Finger-Tipp auf ein Icon ist nicht präziser als der Vollbild-Gewinn wert).
 */
/**
 * Im Overlay-Modus (iPad hoch, < 1024 px) schließt sich die Leiste nach jedem
 * Seitenwechsel — sonst läge sie nach dem Tippen auf einen Bereich weiter über
 * dem Inhalt (Entscheidung Tech-Lead, Qualitäts-Audit 2026-09-28).
 */
export function useCloseSidebarOverlayOnNavigate() {
  const { pathname } = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  useEffect(() => {
    if (isMobile) setOpenMobile(false);
    // Nur beim Seitenwechsel — nicht, wenn der Modus umschaltet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
}

export function AppSidebar() {
  const navItems = useNavItems();
  const groupOrder = useNavGroupOrder();
  const { pathname } = useLocation();
  const isTouchDevice = useIsTouchDevice();
  const branding = usePublicBranding().data?.data;
  const appLogoDataUrl = branding?.appLogoDataUrl ?? null;
  // Basis 38 px bei 100 %; skaliert per CSS-Variable, damit die eingeklappte
  // Icon-Ansicht (!h-8) weiterhin gewinnt (inline-style setzt nur die Variable).
  const appLogoHeightPx = Math.round((38 * (branding?.appLogoScalePercent ?? 100)) / 100);

  const { state } = useSidebar();
  const iconMode = state === 'collapsed';
  useCloseSidebarOverlayOnNavigate();
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(loadCollapsedGroups);

  function setGroupOpen(group: string, open: boolean) {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [group]: !open };
      try {
        localStorage.setItem(GROUP_STATE_KEY, JSON.stringify(next));
      } catch {
        /* localStorage nicht verfügbar — Zustand nur für diese Sitzung */
      }
      return next;
    });
  }

  // Beim Navigieren den Bereich mit der aktiven Seite automatisch aufklappen.
  useEffect(() => {
    const activeGroup = NAV_GROUPS.find((group) =>
      navItems.some((item) => item.group === group && isItemActive(item, pathname)),
    );
    if (activeGroup && collapsedGroups[activeGroup]) setGroupOpen(activeGroup, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Sidebar collapsible={isTouchDevice ? 'offcanvas' : 'icon'} className="hidden tablet:flex">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              tooltip="Dashboard"
              className="h-auto flex-col items-start gap-1.5 py-3 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!py-2 group-data-[collapsible=icon]:!items-center"
            >
              <NavLink to="/">
                {appLogoDataUrl ? (
                  <img
                    src={appLogoDataUrl}
                    alt="Logo"
                    style={{ '--app-logo-h': `${appLogoHeightPx}px` } as CSSProperties}
                    className="h-[var(--app-logo-h)] w-auto max-w-full object-contain group-data-[collapsible=icon]:!h-8"
                  />
                ) : (
                  <span className="flex size-10 items-center justify-center rounded-lg bg-sidebar-primary text-xl font-semibold text-sidebar-primary-foreground group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:text-sm">
                    P
                  </span>
                )}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {groupOrder.map((group) => {
          const groupItems = navItems.filter((item) => item.group === group);
          if (groupItems.length === 0) return null;
          const menu = (
                <SidebarMenu>
                  {/* Icon-Modus: Untermenüs sind ausgeblendet — ein Collapsible-Trigger
                      würde ins Leere klicken. Eltern-Einträge werden dann zum Direktlink
                      auf ihre Übersichtsseite (item.to). */}
                  {groupItems.map((item) =>
                    item.children?.length && !iconMode ? (
                      <Collapsible
                        key={item.to}
                        asChild
                        defaultOpen={isItemActive(item, pathname)}
                        className="group/collapsible"
                      >
                        <SidebarMenuItem>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton
                              tooltip={item.label}
                              isActive={isItemActive(item, pathname)}
                              className={ACTIVE_CLASS}
                            >
                              <item.icon strokeWidth={1.5} aria-hidden />
                              <span>{item.label}</span>
                              {item.children.some((child) => child.to === ANTRAEGE_ROUTE) && (
                                // Signal bleibt sichtbar, wenn das Untermenü zugeklappt ist;
                                // aufgeklappt sitzt der Zähler am Unterpunkt „Anträge".
                                <AntraegeNavBadge className="ml-auto group-data-[state=open]/collapsible:hidden group-data-[collapsible=icon]:hidden" />
                              )}
                              <ChevronRight
                                className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                                aria-hidden
                              />
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {item.children.map((child) => (
                                <SidebarMenuSubItem key={child.to}>
                                  <SidebarMenuSubButton
                                    asChild
                                    isActive={isPathActive(child.to, pathname)}
                                  >
                                    <NavLink to={child.to} end={child.to === item.to}>
                                      <span>{child.label}</span>
                                      {child.to === ANTRAEGE_ROUTE && (
                                        <AntraegeNavBadge className="ml-auto" />
                                      )}
                                    </NavLink>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              ))}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    ) : (
                      <SidebarMenuItem key={item.to}>
                        <SidebarMenuButton
                          asChild
                          isActive={isItemActive(item, pathname)}
                          tooltip={item.label}
                          className={ACTIVE_CLASS}
                        >
                          <NavLink to={item.to} end={item.to === '/' || item.end}>
                            <item.icon strokeWidth={1.5} aria-hidden />
                            <span>{item.label}</span>
                            {item.to === ANTRAEGE_ROUTE && <AntraegeNavBadge className="ml-auto" />}
                          </NavLink>
                        </SidebarMenuButton>
                        {!item.ready && (
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
                            bald
                          </span>
                        )}
                      </SidebarMenuItem>
                    ),
                  )}
                </SidebarMenu>
          );

          // „Übersicht" bleibt ohne Überschrift und immer sichtbar (kein Aufklappen).
          if (group === 'Übersicht') {
            return (
              <SidebarGroup key={group}>
                <SidebarGroupContent>{menu}</SidebarGroupContent>
              </SidebarGroup>
            );
          }

          const groupActive = groupItems.some((item) => isItemActive(item, pathname));
          // Im Icon-Modus immer offen (Labels ausgeblendet, Icons müssen sichtbar bleiben).
          const open = iconMode ? true : groupActive || !collapsedGroups[group];

          return (
            <Collapsible
              key={group}
              open={open}
              onOpenChange={(next) => setGroupOpen(group, next)}
              className="group/nav-group"
            >
              {/* Eingeklappte Bereiche rücken enger zusammen (py-1 + mt-0 nur bei state=closed). */}
              <SidebarGroup className="group-data-[state=closed]/nav-group:py-1">
                {/* Icon-Modus: Label ist nur ausgeblendet (opacity-0, -mt-8) — ohne
                    pointer-events-none fängt der unsichtbare Link Klicks auf dem
                    ersten Icon der Gruppe ab. */}
                <SidebarGroupLabel className="mt-2 flex h-auto min-h-9 items-center gap-1 pr-1 text-sidebar-foreground group-data-[state=closed]/nav-group:mt-0 group-data-[collapsible=icon]:pointer-events-none">
                  {/* Name = Sprung zur Bereichs-Übersicht (Variante B) — präsenter Sektions-Header.
                      Bereiche mit natürlicher Startseite (NAV_GROUP_LANDING) springen direkt
                      dorthin statt auf die generische Hub-Seite (z. B. Technik → Zeichnungsnummern). */}
                  <NavLink
                    to={NAV_GROUP_LANDING[group] ?? `/bereich/${navGroupSlug(group)}`}
                    className={({ isActive }) =>
                      `min-w-0 flex-1 rounded-md text-[15px] font-semibold leading-tight text-sidebar-foreground transition-colors hover:underline hover:underline-offset-4 ${
                        isActive ? 'underline underline-offset-4 decoration-2' : ''
                      }`
                    }
                  >
                    {group}
                  </NavLink>
                  {/* Chevron = Bereich ein-/ausklappen. Auf Touch (iPad) auf 36px
                      angehoben (füllt die h-9-Zeile exakt aus, kein Überlappen mit
                      Nachbarzeilen) — von 24px klar über die 32px-Mindestgrenze. */}
                  <CollapsibleTrigger
                    aria-label={`${group} ein- oder ausklappen`}
                    className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-2 touch:size-9"
                  >
                    <ChevronRight
                      className="size-4 transition-transform duration-200 group-data-[state=open]/nav-group:rotate-90"
                      aria-hidden
                    />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarGroupContent>{menu}</SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          );
        })}
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
        <span className="px-2 pb-1 text-left text-[0.625rem] leading-none text-muted-foreground group-data-[collapsible=icon]:hidden">
          Beta · v{APP_VERSION}
        </span>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
